import { dxtToLocalServerTime, useToken } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	CircularProgress,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FieldError } from "react-hook-form";
import { InspectionSection } from "../models/InspectionSection";
import { getInspectionSections } from "../repositories/InspectionTemplateRepository";

type UseInspectionSectionsProps = {
	shiftId: number | null;
	userId?: number | null;
	onError?: (error: Error) => void;
};

export const useInspectionSections = ({
	shiftId,
	userId,
	onError,
}: UseInspectionSectionsProps) =>
	useQuery<InspectionSection[], Error>({
		queryKey: ["inspection-sections", shiftId, userId],
		queryFn: ({ signal }) =>
			getInspectionSections({ shiftId: shiftId as number, userId }, signal),
		enabled: !!shiftId,
		onError,
	});

const getBlockedReason = (option: InspectionSection) =>
	option.HasTemplate ? null : "No template";

type Props = {
	label?: string;
	value: number | null;
	shiftId: number | null;
	onChange?: (
		sectionId: number | null,
		option?: InspectionSection | null,
	) => void;
	disabled?: boolean;
	required?: boolean;
	fieldError?: FieldError;
	onError?: (error: Error) => void;
};

export const InspectionSectionSelector = ({
	label = "Section",
	value,
	shiftId,
	onChange = () => {},
	disabled = false,
	required = false,
	fieldError,
	onError,
}: Props) => {
	const { getUserId } = useToken();
	const userId = Number(getUserId());

	const { data, isLoading } = useInspectionSections({
		shiftId,
		userId: userId > 0 ? userId : null,
		onError,
	});

	const options = data ?? [];

	const selectedValue =
		options.find((option) => option.SectionId === value) ?? null;

	return (
		<Autocomplete
			id="inspection-section-selector"
			size="small"
			clearOnEscape
			autoHighlight
			options={options}
			loading={isLoading}
			disabled={disabled}
			value={selectedValue}
			isOptionEqualToValue={(option, selected) =>
				option.SectionId === selected.SectionId
			}
			getOptionLabel={(option) => option.SectionName}
			getOptionDisabled={(option) => getBlockedReason(option) !== null}
			onChange={(_event, newValue) =>
				onChange(newValue?.SectionId ?? null, newValue)
			}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props as typeof props & {
					key: string;
				};
				const note =
					getBlockedReason(option) ??
					(option.IsOwnInspection ? "Sheet already opened" : null);

				return (
					<Box
						key={key}
						{...optionProps}
						component="li"
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: 0.25,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								width: "100%",
							}}
						>
							<Box sx={{ flex: 1 }}>{option.SectionName}</Box>
							{note && (
								<Typography variant="caption" color="text.secondary">
									{note}
								</Typography>
							)}
						</Box>
					</Box>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					variant="outlined"
					size="small"
					fullWidth
					error={!!fieldError}
					helperText={fieldError?.message}
					inputProps={{
						...params.inputProps,
						"data-test-id": "inspection-section-selector",
					}}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color="inherit" size={20} />
								) : (
									params.InputProps.endAdornment
								)}
							</>
						),
					}}
				/>
			)}
		/>
	);
};
